import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import moment from "moment"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faSync,
	faPaperPlane,
	faTrashAlt,
	faChevronLeft,
	faChevronRight,
} from "@fortawesome/free-solid-svg-icons"
import {
	URL,
	handleErrors,
	fetchEmployes,
} from "../../store/actions/depannageActions"
import { showToast } from "../../store/actions/toastActions"
import { normalizeApiError } from "../../store/actions/apiHelpers"
import { COLORS, NAV_HEIGHT } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"
import CustomModal from "../basics/interface/CustomModal"
import { filterEmployesActifs } from "../../utilities/employes"
import Loader from "../basics/Loader"
import { useRights } from "../../hooks/rights"
import {
	JOURS_COURTS,
	JOURS_NOMS,
	VIDE,
	libelleEcart,
	libellePrevue,
	libelleSemaine,
	mecaniciens,
	nbAEnvoyer,
	nbModifications,
	premierAvecTaches,
	progression,
	rangerPonctuelles,
	semaineDe,
	tachesDe,
	tvFaite,
} from "./tachesSemaine"

// Page /taches, proposition B (Jordan 31.10.2026) : mécaniciens à gauche
// (progression, « N en retard »), semaine du mécanicien choisi à droite :
// En retard (ponctuelles reportées, GE-82), jour par jour, sans jour fixe,
// véhicules et autres. Payload du POST inchangé.

const JOURS_SELECT = [
	[-1, "Sans jour fixe"],
	...JOURS_NOMS.map((n, i) => [i, n.charAt(0).toUpperCase() + n.slice(1)]),
]

// Tons « en retard » (orangé) et fond du jour J : hors palette COLORS.
const RETARD = { texte: "#9A3412", fond: "#FFF7ED", bord: "#FDBA74" }
const FOND_AUJOURDHUI = "#FAF8F5"
const TRAIT_GRIS = "#D6D3D1"

// En dessous : colonnes empilées (liste puis semaine).
const LARGEUR_EMPILEE = 760

// Filtre « Masquer les employés sans tâche » : mémorisé par navigateur.
const CLE_MASQUER = "taches.masquerSansTache"
const lireMasquer = () => {
	try {
		return window.localStorage.getItem(CLE_MASQUER) !== "0"
	} catch (e) {
		return true
	}
}
const ecrireMasquer = (v) => {
	try {
		window.localStorage.setItem(CLE_MASQUER, v ? "1" : "0")
	} catch (e) {
		// stockage indisponible (navigation privée…) : choix non mémorisé
	}
}

const STATUT = {
	0: { color: COLORS.textMuted, label: "À faire" },
	1: { color: COLORS.gold, label: "En cours" },
	2: { color: COLORS.success, label: "Terminé" },
}

const styles = {
	content: { width: "100%", maxWidth: 1240, margin: "0 auto" },
	head: {
		display: "flex",
		alignItems: "center",
		gap: 20,
		flexWrap: "wrap",
		marginBottom: 20,
	},
	weekNav: { display: "flex", alignItems: "center", gap: 8 },
	roundBtn: {
		width: 34,
		height: 34,
		borderRadius: 17,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: 0,
		cursor: "pointer",
		color: COLORS.text,
		fontSize: 13,
	},
	weekLabel: {
		minWidth: 170,
		textAlign: "center",
		background: "none",
		border: "none",
		cursor: "pointer",
		fontFamily: "inherit",
		color: COLORS.text,
		padding: 0,
	},
	headRight: {
		marginLeft: "auto",
		display: "flex",
		alignItems: "center",
		gap: 12,
		flexWrap: "wrap",
	},
	modifs: { fontSize: 13, color: COLORS.gold, fontWeight: 600 },
	// Écran large : la page tient dans la fenêtre (100vh − nav, cf. piège
	// NAV_HEIGHT de Constants.js) ; en-tête fixe, puis deux colonnes qui
	// défilent CHACUNE de leur côté. Étroit : empilé, liste à ~40 % de la
	// fenêtre avec son propre défilement.
	pageLarge: {
		...gs.page,
		height: `calc(100vh - ${NAV_HEIGHT}px)`,
		minHeight: 0,
		overflow: "hidden",
		paddingBottom: 16,
	},
	contentLarge: {
		display: "flex",
		flexDirection: "column",
		height: "100%",
		minHeight: 0,
	},
	layout: (etroit) => ({
		display: "flex",
		flexWrap: etroit ? "wrap" : "nowrap",
		gap: 20,
		alignItems: "flex-start",
		...(etroit ? {} : { flex: 1, minHeight: 0 }),
	}),
	// Colonne de gauche : filtre (fixe) + liste qui défile seule.
	colonne: (etroit) => ({
		display: "flex",
		flexDirection: "column",
		gap: 8,
		minHeight: 0,
		...(etroit
			? { flex: "1 1 100%" }
			: { flex: "0 0 270px", maxHeight: "100%" }),
	}),
	nav: (etroit) => ({
		position: "relative",
		display: "flex",
		flexDirection: "column",
		gap: 6,
		overflowY: "auto",
		boxSizing: "border-box",
		...(etroit
			? { maxHeight: "40vh" }
			: { flex: "1 1 auto", minHeight: 0, paddingRight: 4 }),
	}),
	filtre: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		fontSize: 13,
		color: COLORS.textMuted,
		padding: "0 14px",
		cursor: "pointer",
		userSelect: "none",
	},
	personne: {
		padding: "12px 14px",
		fontSize: 13,
		color: COLORS.textLight,
	},
	lienFiltre: {
		display: "block",
		marginTop: 6,
		background: "none",
		border: "none",
		padding: 0,
		fontFamily: "inherit",
		fontSize: 13,
		fontWeight: 600,
		color: COLORS.accent,
		cursor: "pointer",
	},
	mecano: (actif, zone) => ({
		textAlign: "left",
		fontFamily: "inherit",
		backgroundColor: zone
			? COLORS.accentLight
			: actif
				? COLORS.surface
				: "transparent",
		border: zone
			? `1px dashed ${COLORS.accent}`
			: `1px solid ${actif ? COLORS.text : "transparent"}`,
		borderRadius: 12,
		padding: "12px 14px",
		color: COLORS.text,
		cursor: "pointer",
		width: "100%",
		// Dans une colonne qui défile : pas d'écrasement des entrées.
		flexShrink: 0,
	}),
	mecanoTop: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "baseline",
		gap: 8,
	},
	small: { fontSize: 12, color: COLORS.textMuted, marginTop: 2 },
	traits: (n) => ({
		display: "grid",
		gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`,
		gap: 3,
		marginTop: 8,
	}),
	trait: (fait) => ({
		height: 5,
		borderRadius: 3,
		backgroundColor: fait ? COLORS.success : TRAIT_GRIS,
	}),
	main: (etroit) => ({
		minWidth: 0,
		backgroundColor: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 16,
		padding: "20px 24px",
		boxSizing: "border-box",
		...(etroit
			? { flex: "1 1 100%" }
			: { flex: "1 1 auto", maxHeight: "100%", overflowY: "auto" }),
	}),
	mainHead: {
		display: "flex",
		alignItems: "center",
		gap: 12,
		flexWrap: "wrap",
		marginBottom: 8,
	},
	sendBtn: {
		marginLeft: "auto",
		fontFamily: "inherit",
		fontSize: 13,
		fontWeight: 600,
		padding: "8px 12px",
		borderRadius: 8,
		border: `1px solid ${COLORS.accent}`,
		backgroundColor: COLORS.surface,
		color: COLORS.accent,
		display: "inline-flex",
		alignItems: "center",
		gap: 6,
		cursor: "pointer",
	},
	retard: {
		marginTop: 14,
		border: `1px solid ${RETARD.bord}`,
		backgroundColor: RETARD.fond,
		borderRadius: 12,
		padding: "12px 14px",
	},
	retardTitre: {
		fontSize: 14,
		fontWeight: 700,
		color: RETARD.texte,
		marginBottom: 8,
	},
	retardLigne: {
		display: "flex",
		alignItems: "center",
		gap: 12,
		flexWrap: "wrap",
		backgroundColor: COLORS.surface,
		borderRadius: 8,
		padding: "10px 12px",
		marginTop: 6,
	},
	prevue: {
		fontSize: 12,
		fontWeight: 600,
		color: RETARD.texte,
		width: 120,
	},
	sectionTitre: { marginTop: 20, fontSize: 14, fontWeight: 700 },
	jours: { marginTop: 8, borderTop: `1px solid ${COLORS.border}` },
	jour: (auj) => ({
		display: "grid",
		gridTemplateColumns: "110px minmax(0, 1fr)",
		borderBottom: `1px solid ${COLORS.border}`,
		backgroundColor: auj ? FOND_AUJOURDHUI : "transparent",
	}),
	jourLib: (auj) => ({
		padding: auj ? "12px 0 12px 8px" : "12px 0",
		fontSize: 13,
		fontWeight: auj ? 700 : 600,
		color: auj ? COLORS.text : COLORS.textMuted,
	}),
	jourContenu: (auj) => ({
		padding: auj ? "8px 8px 8px 0" : "8px 0",
		display: "flex",
		flexDirection: "column",
		gap: 6,
		minWidth: 0,
	}),
	rien: { padding: "4px 0", fontSize: 13, color: COLORS.textLight },
	ligne: {
		display: "flex",
		alignItems: "center",
		gap: 12,
		flexWrap: "wrap",
		padding: "4px 0",
	},
	titre: { fontSize: 14, flex: "1 1 180px", minWidth: 0 },
	statut: (etat) => ({
		fontSize: 12,
		fontWeight: 600,
		color: (STATUT[etat] || STATUT[0]).color,
		whiteSpace: "nowrap",
	}),
	pasEnvoyee: { fontSize: 12, color: COLORS.textMuted, whiteSpace: "nowrap" },
	notesBureau: { fontSize: 13, color: COLORS.textMuted, marginTop: 2 },
	noteMeca: {
		fontSize: 13,
		color: COLORS.disabledText,
		backgroundColor: COLORS.bg,
		borderRadius: 8,
		padding: "8px 10px",
		marginTop: 4,
	},
	jourSelect: {
		padding: "4px 6px",
		borderRadius: 6,
		border: `1px solid ${COLORS.border}`,
		fontSize: 12,
		fontFamily: "inherit",
		backgroundColor: COLORS.surface,
		color: COLORS.text,
	},
	avion: (on) => ({
		border: "none",
		background: "none",
		cursor: "pointer",
		padding: 2,
		fontSize: 12,
		color: on ? COLORS.accent : COLORS.borderStrong,
	}),
	addBtn: {
		alignSelf: "flex-start",
		fontFamily: "inherit",
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.textMuted,
		background: "none",
		border: `1px dashed ${COLORS.borderStrong}`,
		borderRadius: 8,
		padding: "6px 10px",
		cursor: "pointer",
	},
	lienDiscret: {
		alignSelf: "flex-start",
		fontFamily: "inherit",
		fontSize: 12,
		color: COLORS.textLight,
		background: "none",
		border: "none",
		padding: 0,
		cursor: "pointer",
	},
	inlineInput: {
		width: "100%",
		boxSizing: "border-box",
		border: `1px solid ${COLORS.accent}`,
		borderRadius: 6,
		padding: "4px 6px",
		fontSize: 13,
		fontFamily: "inherit",
	},
	editable: (canEdit) => ({ cursor: canEdit ? "text" : "default" }),
	bas: { display: "flex", flexWrap: "wrap", gap: 20, marginTop: 24 },
	carte: {
		border: `1px solid ${COLORS.border}`,
		borderRadius: 12,
		padding: "4px 14px",
	},
	carteLigne: (last) => ({
		padding: "10px 0",
		borderBottom: last ? "none" : `1px solid ${COLORS.bg}`,
		cursor: "grab",
	}),
	items: {
		display: "flex",
		flexWrap: "wrap",
		gap: 14,
		marginTop: 6,
		fontSize: 13,
	},
	itemLabel: { display: "flex", alignItems: "center", gap: 6 },
	vide: { fontSize: 13, color: COLORS.textLight, margin: "10px 0 4px" },
	state: {
		padding: "48px 16px",
		textAlign: "center",
		color: COLORS.textLight,
		fontSize: 14,
	},
}

// Texte modifiable en place (clic → champ). Hors du composant de page : un
// composant défini dans le render perdrait le focus à chaque frappe.
const TexteEnPlace = ({ value, onChange, canEdit, vide, style }) => {
	const [edition, setEdition] = useState(false)
	if (edition)
		return (
			<input
				type="text"
				autoFocus
				style={styles.inlineInput}
				value={value || ""}
				onChange={(e) => onChange(e.target.value)}
				onBlur={() => setEdition(false)}
				onKeyUp={(e) => e.key === "Enter" && setEdition(false)}
			/>
		)
	return (
		<span
			style={{ ...style, ...styles.editable(canEdit) }}
			onClick={() => canEdit && setEdition(true)}
		>
			{value || <span style={{ color: COLORS.textLight }}>{vide}</span>}
		</span>
	)
}

const Corbeille = ({ onClick }) => (
	<button
		type="button"
		aria-label="Supprimer la tâche"
		title="Supprimer"
		style={styles.avion(false)}
		onClick={onClick}
	>
		<FontAwesomeIcon icon={faTrashAlt} />
	</button>
)

const Avion = ({ on, onClick }) => (
	<button
		type="button"
		style={styles.avion(on)}
		title={on ? "Envoyée" : "Non envoyée"}
		onClick={onClick}
	>
		<FontAwesomeIcon icon={faPaperPlane} />
	</button>
)

// Une ponctuelle (jour par jour / sans jour fixe).
const LignePonctuelle = ({
	td,
	nomEmp,
	droits,
	onField,
	onSupprimer,
	drag,
}) => {
	const [noteMeca, setNoteMeca] = useState(false)
	return (
		<div {...drag}>
			<div style={styles.ligne}>
				<TexteEnPlace
					value={td.titre}
					vide="Sans titre"
					canEdit={droits.edition}
					onChange={(v) => onField("titre", v)}
					style={{
						...styles.titre,
						fontWeight: td.etat === 2 ? 400 : 600,
						color: td.etat === 2 ? COLORS.textMuted : COLORS.text,
					}}
				/>
				{!td.envoye && (
					<span style={styles.pasEnvoyee}>Pas encore envoyée</span>
				)}
				<span style={styles.statut(td.etat)}>
					{(STATUT[td.etat] || STATUT[0]).label}
				</span>
				<select
					aria-label="Jour"
					style={styles.jourSelect}
					value={td.delta_day}
					disabled={!droits.edition}
					onChange={(e) =>
						onField("delta_day", parseInt(e.target.value, 10))
					}
				>
					{JOURS_SELECT.map(([val, lib]) => (
						<option key={val} value={val}>
							{lib}
						</option>
					))}
				</select>
				{droits.boss && (
					<Avion
						on={td.envoye}
						onClick={() => onField("envoye", !td.envoye)}
					/>
				)}
				{droits.ajout && <Corbeille onClick={onSupprimer} />}
			</div>
			{(td.notes ||
				droits.edition ||
				(droits.boss && !td.notes_employe && !noteMeca)) && (
				<div
					style={{ ...styles.notesBureau, display: "flex", gap: 14 }}
				>
					{(td.notes || droits.edition) && (
						<TexteEnPlace
							value={td.notes}
							vide="+ note du bureau"
							canEdit={droits.edition}
							onChange={(v) => onField("notes", v)}
						/>
					)}
					{droits.boss && !td.notes_employe && !noteMeca && (
						<button
							type="button"
							style={styles.lienDiscret}
							onClick={() => setNoteMeca(true)}
						>
							+ note de {nomEmp}
						</button>
					)}
				</div>
			)}
			{(td.notes_employe || noteMeca) && (
				<div style={styles.noteMeca}>
					Note de {nomEmp} :{" "}
					<TexteEnPlace
						value={td.notes_employe}
						vide="—"
						canEdit={droits.boss}
						onChange={(v) => onField("notes_employe", v)}
					/>
				</div>
			)}
		</div>
	)
}

const TachesV2 = () => {
	const dispatch = useDispatch()
	const employes = useSelector((s) => s.masterReducer.employes)
	const login = useSelector((s) => s.navInfo.login)
	const token = login.token
	const groups = login.groups || []
	const { hasRight } = useRights()

	// Lecture ET écriture pour les groupes qui ont la route (décision Jordan
	// 30.07.2026 : pas de second niveau de droit dans la page). Boss seul :
	// cases (items, « fait »), envoi, note du mécanicien, ajout de ponctuelle.
	const droits = {
		edition: hasRight("taches"),
		boss: groups.includes("boss"),
		// Supprimer suit le droit d'ajouter (Jordan 31.10) : boss aujourd'hui.
		ajout: groups.includes("boss"),
	}

	const [dates, setDates] = useState(() => semaineDe(moment()))
	const [taches, setTaches] = useState({})
	// État chargé (base du compteur « modifications non enregistrées »).
	const [charge, setCharge] = useState({})
	const [choisi, setChoisi] = useState(0)
	const [masquer, setMasquerEtat] = useState(lireMasquer)
	const setMasquer = (v) => {
		ecrireMasquer(v)
		setMasquerEtat(v)
	}
	// Choix par défaut posé une fois par semaine chargée, tant que
	// l'utilisateur n'a pas choisi lui-même un mécanicien.
	const choixManuel = useRef(false)
	const defautPose = useRef(false)
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [confirmSave, setConfirmSave] = useState(false)
	const [zone, setZone] = useState(0)
	// Suppression d'une tâche enregistrée : { empId, td } puis modal.
	const [aSupprimer, setASupprimer] = useState(null)
	const [suppression, setSuppression] = useState({
		enCours: false,
		erreur: "",
	})
	const semaineRef = useRef(null)
	const listeRef = useRef(null)
	const [etroit, setEtroit] = useState(
		() => window.innerWidth < LARGEUR_EMPILEE,
	)
	useEffect(() => {
		const surResize = () => setEtroit(window.innerWidth < LARGEUR_EMPILEE)
		window.addEventListener("resize", surResize)
		return () => window.removeEventListener("resize", surResize)
	}, [])

	useEffect(() => {
		dispatch(fetchEmployes())
	}, [dispatch])

	// (Re)chargement à chaque changement de semaine.
	useEffect(() => {
		setLoading(true)
		defautPose.current = false
		fetch(
			`${URL}api/taches?debut=${dates.startDate.format(
				"DD-MM-YYYY",
			)}&fin=${dates.endDate.format("DD-MM-YYYY")}`,
			{ headers: { Authorization: `Bearer ${token}` } },
		)
			.then(handleErrors)
			.then((r) => r.json())
			.then((data) => {
				setTaches(data)
				setCharge(data)
			})
			.catch(() => dispatch(showToast("danger", "Échec du chargement.")))
			.finally(() => setLoading(false))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dates])

	const liste = useMemo(
		() => mecaniciens(filterEmployesActifs(employes), taches, masquer),
		[employes, taches, masquer],
	)

	// Sélection par défaut : le premier mécanicien qui a des tâches (gardée
	// d'une semaine à l'autre tant qu'il est dans la liste).
	useEffect(() => {
		if (loading || liste.length === 0) return
		const absent = !liste.some((e) => e.id === choisi)
		if (absent || (!choixManuel.current && !defautPose.current))
			setChoisi(premierAvecTaches(liste, taches))
		defautPose.current = true
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [liste, loading])

	// Au chargement (choix automatique), la liste défile jusqu'au mécanicien
	// sélectionné — sans faire défiler la page.
	useEffect(() => {
		if (loading || choixManuel.current) return
		window.requestAnimationFrame(() => {
			const nav = listeRef.current
			const btn = nav && nav.querySelector('[aria-current="true"]')
			if (btn) nav.scrollTop = Math.max(0, btn.offsetTop - 8)
		})
	}, [choisi, loading])

	const modifs = useMemo(
		() => nbModifications(charge, taches),
		[charge, taches],
	)

	const changerSemaine = (n) =>
		setDates(semaineDe(dates.startDate.clone().add(n, "weeks")))

	// ---- Mutations locales ----
	const majListe = (empId, type, fn) =>
		setTaches((prev) => {
			const emp = prev[empId] || VIDE
			return { ...prev, [empId]: { ...emp, [type]: fn(emp[type]) } }
		})
	const majLigne = (empId, type, index, patch) =>
		majListe(empId, type, (l) =>
			l.map((row, i) => (i === index ? { ...row, ...patch(row) } : row)),
		)
	const toggleItem = (empId, tvIndex, itemId) =>
		majLigne(empId, "tv", tvIndex, (row) => ({
			items: row.items.map((it) =>
				it.id === itemId ? { ...it, done: !it.done } : it,
			),
		}))
	const ajouter = (empId, deltaDay) =>
		majListe(empId, "td", (l) => [
			...l,
			{
				id: 0,
				date: dates.startDate.format("DD-MM-YYYY"),
				titre: "",
				etat: 0,
				notes: "",
				notes_employe: "",
				delta_day: deltaDay,
				employe: empId,
				envoye: false,
				reportee: false,
			},
		])
	const envoyerTout = (empId) =>
		setTaches((prev) => {
			const emp = prev[empId] || VIDE
			const mark = (l) => l.map((r) => ({ ...r, envoye: true }))
			return {
				...prev,
				[empId]: {
					tv: mark(emp.tv),
					ta: mark(emp.ta),
					td: mark(emp.td),
				},
			}
		})

	// ---- Suppression d'une ponctuelle ----
	// Jamais enregistrée (id 0) : retrait local. Enregistrée : modal, DELETE
	// immédiat, puis retrait de l'état local ET de l'état chargé (le compteur
	// de modifications ne bouge pas, les autres modifications restent).
	const supprimer = (empId, index) => {
		const td = tachesDe(taches, empId).td[index]
		if (!td) return
		if (!td.id) {
			majListe(empId, "td", (l) => l.filter((_, i) => i !== index))
			return
		}
		setSuppression({ enCours: false, erreur: "" })
		setASupprimer({ empId, td })
	}
	const sansTache = (etat, id) =>
		Object.fromEntries(
			Object.entries(etat).map(([k, t]) => [
				k,
				{ ...t, td: (t.td || []).filter((x) => x.id !== id) },
			]),
		)
	const confirmerSuppression = () => {
		const id = aSupprimer.td.id
		setSuppression({ enCours: true, erreur: "" })
		fetch(`${URL}api/taches`, {
			method: "delete",
			headers: {
				Accept: "application/json, text/plain",
				"Content-type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({ id }),
		})
			.then(handleErrors)
			.then(() => {
				setTaches((prev) => sansTache(prev, id))
				setCharge((prev) => sansTache(prev, id))
				setASupprimer(null)
				setSuppression({ enCours: false, erreur: "" })
				dispatch(showToast("success", "Tâche supprimée"))
			})
			.catch(async (err) => {
				// Erreur affichée dans la modal ouverte (pas de toast).
				const e = await normalizeApiError(err)
				setSuppression({ enCours: false, erreur: e.message })
			})
	}
	// Envoyée selon l'état chargé : c'est ce que la tablette a reçu.
	const envoyeeChargee = (td) =>
		Object.values(charge).some((t) =>
			(t.td || []).some((x) => x.id === td.id && x.envoye),
		)

	// ---- Glisser une tâche sur un mécanicien de la liste (réassignation) ----
	const drag = (empId, type, index) => ({
		draggable: true,
		onDragStart: (e) => {
			e.dataTransfer.setData("emp", String(empId))
			e.dataTransfer.setData("type", type)
			e.dataTransfer.setData("index", String(index))
		},
	})
	const drop = (e, versId) => {
		e.preventDefault()
		setZone(0)
		const de = parseInt(e.dataTransfer.getData("emp"), 10)
		const type = e.dataTransfer.getData("type")
		const index = parseInt(e.dataTransfer.getData("index"), 10)
		if (!type || Number.isNaN(index) || de === versId) return
		setTaches((prev) => {
			const source = (prev[de] || VIDE)[type]
			if (!source || !source[index]) return prev
			const cible = prev[versId] || VIDE
			return {
				...prev,
				[de]: {
					...prev[de],
					[type]: source.filter((_, i) => i !== index),
				},
				[versId]: {
					...cible,
					[type]: [
						...cible[type],
						{ ...source[index], employe: versId },
					],
				},
			}
		})
	}

	// ---- Save ----
	const doSave = () => {
		setSaving(true)
		fetch(`${URL}api/taches`, {
			method: "post",
			headers: {
				Accept: "application/json, text/plain",
				"Content-type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({
				taches,
				debut: dates.startDate.format("DD-MM-YYYY"),
				fin: dates.endDate.format("DD-MM-YYYY"),
				employe: choisi,
			}),
		})
			.then(handleErrors)
			.then((r) => r.json())
			.then((data) => {
				setTaches(data.taches)
				setCharge(data.taches)
				setConfirmSave(false)
				dispatch(showToast("success", "Tâches enregistrées"))
			})
			.catch(() =>
				dispatch(showToast("danger", "Échec de l'enregistrement.")),
			)
			.finally(() => setSaving(false))
	}

	// ---- Rendu ----
	const mecano = (emp) => {
		const p = progression(tachesDe(taches, emp.id))
		const actif = emp.id === choisi
		return (
			<button
				key={emp.id}
				type="button"
				aria-current={actif ? "true" : undefined}
				style={styles.mecano(actif, zone === emp.id)}
				onClick={() => {
					choixManuel.current = true
					setChoisi(emp.id)
					// La semaine revient en haut de SA colonne.
					if (semaineRef.current) semaineRef.current.scrollTop = 0
				}}
				onDragOver={(e) => {
					e.preventDefault()
					if (zone !== emp.id) setZone(emp.id)
				}}
				onDragLeave={() => setZone(0)}
				onDrop={(e) => drop(e, emp.id)}
			>
				<div style={styles.mecanoTop}>
					<span
						style={{ fontSize: 15, fontWeight: actif ? 700 : 600 }}
					>
						{emp.nom}
					</span>
					{p.enRetard > 0 ? (
						<span
							style={{
								fontSize: 12,
								fontWeight: 600,
								color: RETARD.texte,
							}}
						>
							{p.enRetard} en retard
						</span>
					) : p.total > 0 && p.faites === p.total ? (
						<span
							style={{
								fontSize: 12,
								fontWeight: 600,
								color: COLORS.success,
							}}
						>
							Tout fait
						</span>
					) : null}
				</div>
				<div style={styles.small}>
					{p.total === 0
						? "Aucune tâche"
						: `${p.faites} ${p.faites > 1 ? "faites" : "faite"} sur ${p.total}`}
				</div>
				{p.total > 0 && (
					<div style={styles.traits(p.total)}>
						{p.traits.map((fait, i) => (
							<div key={i} style={styles.trait(fait)} />
						))}
					</div>
				)}
			</button>
		)
	}

	const semaine = (emp) => {
		const t = tachesDe(taches, emp.id)
		const p = progression(t)
		const { enRetard, parJour, sansJour } = rangerPonctuelles(t.td)
		const aEnvoyer = nbAEnvoyer(t)
		const aujourdhui = moment().format("DD-MM-YYYY")
		const onField = (index) => (field, value) =>
			majLigne(emp.id, "td", index, () => ({ [field]: value }))
		const ponctuelle = ({ td, index }) => (
			<LignePonctuelle
				key={`td_${td.id}_${index}`}
				td={td}
				nomEmp={emp.nom}
				droits={droits}
				onField={onField(index)}
				onSupprimer={() => supprimer(emp.id, index)}
				drag={drag(emp.id, "td", index)}
			/>
		)
		const jours = [0, 1, 2, 3, 4, 5, 6].filter(
			(j) => j < 5 || parJour[j].length > 0,
		)
		return (
			<main ref={semaineRef} style={styles.main(etroit)}>
				<div style={styles.mainHead}>
					<h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
						Semaine de {emp.nom}
					</h3>
					{droits.boss && aEnvoyer > 0 && (
						<button
							type="button"
							style={styles.sendBtn}
							onClick={() => envoyerTout(emp.id)}
						>
							<FontAwesomeIcon icon={faPaperPlane} /> Envoyer{" "}
							{aEnvoyer} {aEnvoyer > 1 ? "tâches" : "tâche"} à{" "}
							{emp.nom}
						</button>
					)}
				</div>
				{p.total === 0 && (
					<div style={styles.vide}>Aucune tâche cette semaine.</div>
				)}

				{enRetard.length > 0 && (
					<div style={styles.retard}>
						<div style={styles.retardTitre}>En retard</div>
						{enRetard.map(({ td, index }) => (
							<div
								key={`r_${td.id}_${index}`}
								style={styles.retardLigne}
								{...drag(emp.id, "td", index)}
							>
								<span style={styles.prevue}>
									{libellePrevue(td)}
								</span>
								<TexteEnPlace
									value={td.titre}
									vide="Sans titre"
									canEdit={droits.edition}
									onChange={(v) => onField(index)("titre", v)}
									style={{ ...styles.titre, fontWeight: 600 }}
								/>
								<span style={styles.statut(td.etat)}>
									{(STATUT[td.etat] || STATUT[0]).label}
								</span>
								{droits.ajout && (
									<Corbeille
										onClick={() => supprimer(emp.id, index)}
									/>
								)}
							</div>
						))}
					</div>
				)}

				<div style={styles.sectionTitre}>Jour par jour</div>
				<div style={styles.jours}>
					{jours.map((j) => {
						const date = dates.startDate.clone().add(j, "days")
						const auj = date.format("DD-MM-YYYY") === aujourdhui
						return (
							<div key={j} style={styles.jour(auj)} data-jour={j}>
								<div style={styles.jourLib(auj)}>
									{JOURS_COURTS[j]} {date.date()}
									{auj && (
										<>
											<br />
											<span
												style={{
													fontWeight: 500,
													color: COLORS.textMuted,
												}}
											>
												aujourd'hui
											</span>
										</>
									)}
								</div>
								<div style={styles.jourContenu(auj)}>
									{parJour[j].length === 0 && (
										<div style={styles.rien}>
											Rien de prévu
										</div>
									)}
									{parJour[j].map(ponctuelle)}
									{droits.boss && (
										<button
											type="button"
											style={styles.addBtn}
											onClick={() => ajouter(emp.id, j)}
										>
											+ Ajouter une tâche {JOURS_NOMS[j]}
										</button>
									)}
								</div>
							</div>
						)
					})}
					<div style={styles.jour(false)} data-jour="-1">
						<div style={styles.jourLib(false)}>Sans jour fixe</div>
						<div style={styles.jourContenu(false)}>
							{sansJour.length === 0 && (
								<div style={styles.rien}>Rien de prévu</div>
							)}
							{sansJour.map(ponctuelle)}
							{droits.boss && (
								<button
									type="button"
									style={styles.addBtn}
									onClick={() => ajouter(emp.id, -1)}
								>
									+ Ajouter une tâche sans jour fixe
								</button>
							)}
						</div>
					</div>
				</div>

				{(t.tv.length > 0 || t.ta.length > 0) && (
					<div style={styles.bas}>
						{t.tv.length > 0 && (
							<section style={{ flex: "2 1 340px", minWidth: 0 }}>
								<div
									style={{
										...styles.sectionTitre,
										marginTop: 0,
										marginBottom: 8,
									}}
								>
									Véhicules
								</div>
								<div style={styles.carte}>
									{t.tv.map((tv, index) => {
										const items = tv.items || []
										const faits = items.filter(
											(i) => i.done,
										).length
										const fait = tvFaite(tv)
										return (
											<div
												key={`tv_${tv.id}_${index}`}
												style={styles.carteLigne(
													index === t.tv.length - 1,
												)}
												{...drag(emp.id, "tv", index)}
											>
												<div
													style={{
														...styles.ligne,
														padding: 0,
													}}
												>
													<span
														style={{
															...styles.titre,
															fontWeight: 600,
															color: fait
																? COLORS.textMuted
																: COLORS.text,
														}}
													>
														{tv.veh_nom}
													</span>
													<span
														style={styles.statut(
															fait
																? 2
																: faits > 0
																	? 1
																	: 0,
														)}
													>
														{faits} sur{" "}
														{items.length}
													</span>
													{droits.boss && (
														<Avion
															on={tv.envoye}
															onClick={() =>
																majLigne(
																	emp.id,
																	"tv",
																	index,
																	(r) => ({
																		envoye: !r.envoye,
																	}),
																)
															}
														/>
													)}
												</div>
												{/* Faite : cases masquées (maquette), sauf pour le boss
												    qui peut encore décocher. */}
												{(!fait || droits.boss) &&
													items.length > 0 && (
														<div
															style={styles.items}
														>
															{items.map(
																(item) => (
																	<label
																		key={
																			item.id
																		}
																		style={
																			styles.itemLabel
																		}
																	>
																		<input
																			type="checkbox"
																			disabled={
																				!droits.boss
																			}
																			checked={
																				item.done
																			}
																			onChange={() =>
																				toggleItem(
																					emp.id,
																					index,
																					item.id,
																				)
																			}
																		/>
																		{
																			item.nom
																		}
																	</label>
																),
															)}
														</div>
													)}
											</div>
										)
									})}
								</div>
							</section>
						)}
						{t.ta.length > 0 && (
							<section style={{ flex: "1 1 220px", minWidth: 0 }}>
								<div
									style={{
										...styles.sectionTitre,
										marginTop: 0,
										marginBottom: 8,
									}}
								>
									Autres
								</div>
								<div style={styles.carte}>
									{t.ta.map((ta, index) => (
										<div
											key={`ta_${ta.id}_${index}`}
											style={{
												...styles.ligne,
												...styles.carteLigne(
													index === t.ta.length - 1,
												),
												color: ta.done
													? COLORS.textMuted
													: COLORS.text,
											}}
											{...drag(emp.id, "ta", index)}
										>
											<span
												style={{
													fontSize: 14,
													flex: 1,
												}}
											>
												{ta.texte}
											</span>
											<label
												style={{
													...styles.itemLabel,
													...styles.statut(
														ta.done ? 2 : 0,
													),
												}}
											>
												<input
													type="checkbox"
													disabled={!droits.boss}
													checked={!!ta.done}
													onChange={() =>
														majLigne(
															emp.id,
															"ta",
															index,
															(r) => ({
																done: !r.done,
															}),
														)
													}
												/>
												{ta.done ? "Fait" : "À faire"}
											</label>
											{droits.boss && (
												<Avion
													on={ta.envoye}
													onClick={() =>
														majLigne(
															emp.id,
															"ta",
															index,
															(r) => ({
																envoye: !r.envoye,
															}),
														)
													}
												/>
											)}
										</div>
									))}
								</div>
							</section>
						)}
					</div>
				)}
			</main>
		)
	}

	const selection = liste.find((e) => e.id === choisi)

	return (
		<div style={etroit ? gs.page : styles.pageLarge}>
			<div
				style={
					etroit
						? styles.content
						: { ...styles.content, ...styles.contentLarge }
				}
			>
				{confirmSave && (
					<CustomModal
						title="Enregistrer les tâches ?"
						onClose={
							saving ? () => {} : () => setConfirmSave(false)
						}
						showCloseButton={!saving}
						buttons={[
							{
								title: "Annuler",
								styleName: "secondary",
								onClick: () => setConfirmSave(false),
								disabled: saving,
							},
							{
								title: saving ? (
									<FontAwesomeIcon icon={faSync} spin />
								) : (
									"Enregistrer"
								),
								styleName: "primary",
								onClick: doSave,
								disabled: saving,
							},
						]}
					>
						<div style={{ fontSize: 14, color: COLORS.textMuted }}>
							Les modifications de la semaine seront enregistrées
							(les tâches marquées « envoyé » déclenchent une
							notification).
						</div>
					</CustomModal>
				)}

				{aSupprimer && (
					<CustomModal
						title={`Supprimer la tâche « ${
							aSupprimer.td.titre || "Sans titre"
						} » ?`}
						onClose={
							suppression.enCours
								? () => {}
								: () => setASupprimer(null)
						}
						showCloseButton={!suppression.enCours}
						buttons={[
							{
								title: "Annuler",
								styleName: "secondary",
								onClick: () => setASupprimer(null),
								disabled: suppression.enCours,
							},
							{
								title: suppression.enCours ? (
									<FontAwesomeIcon icon={faSync} spin />
								) : (
									"Supprimer"
								),
								styleName: "danger",
								onClick: confirmerSuppression,
								disabled: suppression.enCours,
							},
						]}
					>
						<div style={{ fontSize: 14, color: COLORS.textMuted }}>
							{envoyeeChargee(aSupprimer.td)
								? `Elle disparaît aussi de la tablette de ${
										(
											liste.find(
												(e) =>
													e.id === aSupprimer.empId,
											) || { nom: "l'employé" }
										).nom
									}.`
								: "La suppression est immédiate."}
						</div>
						{suppression.erreur && (
							<div
								role="alert"
								style={{
									marginTop: 12,
									fontSize: 13,
									color: COLORS.danger,
								}}
							>
								{suppression.erreur}
							</div>
						)}
					</CustomModal>
				)}

				<div style={styles.head}>
					<h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
						Tâches
					</h2>
					<div style={styles.weekNav}>
						<button
							type="button"
							aria-label="Semaine précédente"
							style={styles.roundBtn}
							onClick={() => changerSemaine(-1)}
						>
							<FontAwesomeIcon icon={faChevronLeft} />
						</button>
						<button
							type="button"
							title="Revenir à la semaine en cours"
							style={styles.weekLabel}
							onClick={() => setDates(semaineDe(moment()))}
						>
							<div style={{ fontSize: 15, fontWeight: 700 }}>
								{libelleSemaine(dates.startDate)}
							</div>
							<div
								style={{
									fontSize: 12,
									color: COLORS.textMuted,
								}}
							>
								{libelleEcart(dates.startDate)}
							</div>
						</button>
						<button
							type="button"
							aria-label="Semaine suivante"
							style={styles.roundBtn}
							onClick={() => changerSemaine(1)}
						>
							<FontAwesomeIcon icon={faChevronRight} />
						</button>
					</div>
					{droits.edition && (
						<div style={styles.headRight}>
							{modifs > 0 && (
								<span style={styles.modifs}>
									{modifs}{" "}
									{modifs > 1
										? "modifications non enregistrées"
										: "modification non enregistrée"}
								</span>
							)}
							<button
								type="button"
								style={{
									...gs.btnPrimary,
									borderRadius: 8,
									padding: "10px 18px",
									fontWeight: 600,
								}}
								onClick={() => setConfirmSave(true)}
							>
								Enregistrer
							</button>
						</div>
					)}
				</div>

				{loading ? (
					<Loader variant="ring" message="Chargement…" />
				) : (
					<div style={styles.layout(etroit)}>
						<div style={styles.colonne(etroit)}>
							<label style={styles.filtre}>
								<input
									type="checkbox"
									checked={masquer}
									onChange={(e) =>
										setMasquer(e.target.checked)
									}
								/>
								Masquer les employés sans tâche
							</label>
							<nav
								ref={listeRef}
								aria-label="Mécaniciens"
								style={styles.nav(etroit)}
							>
								{liste.map(mecano)}
								{liste.length === 0 && (
									<div style={styles.personne}>
										{masquer
											? "Aucun employé n'a de tâche cette semaine."
											: "Aucun employé actif."}
										{masquer && (
											<button
												type="button"
												style={styles.lienFiltre}
												onClick={() =>
													setMasquer(false)
												}
											>
												Afficher tous les employés
											</button>
										)}
									</div>
								)}
							</nav>
						</div>
						{selection
							? semaine(selection)
							: liste.length > 0 && (
									<main style={styles.main(etroit)}>
										<div style={styles.state}>
											Aucun mécanicien sélectionné.
										</div>
									</main>
								)}
					</div>
				)}
			</div>
		</div>
	)
}

export default TachesV2
